import { useEffect } from 'react';
import {
  Links,
  Meta,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useLocation,
  useMatches,
  useRouteError,
} from 'react-router';
import 'leaflet/dist/leaflet.css';
import './index.css';
import './styles/bespoke.css';
import SiteShell from './components/SiteShell';
import ErrorFallback from './components/ErrorFallback';
import NotFoundPage from './pages/NotFoundPage';
import { ThemeProvider, ThemeStyle, ThemedBody } from './context/ThemeProvider';
import { NOT_FOUND_META } from './content/siteMetadata';
import { trackPageView } from './utils/analytics';

const FAVICON =
  "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='.9em' font-size='90'>🍺</text></svg>";

// Google tag (gtag.js) with Consent Mode v2. Analytics stays denied until the app explicitly
// grants consent (see src/components/RegionNotice.jsx); gtag.js only loads on grant.
const GTAG_BOOTSTRAP = `
window.dataLayer = window.dataLayer || [];
window.__dayforitAnalyticsLoaded = false;
window.__dayforitAnalyticsLoading = false;
window.__dayforitMeasurementId = 'G-MSSX9TZDCR';

function gtag(){window.dataLayer.push(arguments);}
window.gtag = window.gtag || gtag;

window.gtag('consent', 'default', {
  'analytics_storage': 'denied',
  'ad_storage': 'denied',
  'wait_for_update': 500
});

window.__dayforitGrantAnalytics = function () {
  if (window.__dayforitAnalyticsLoaded || window.__dayforitAnalyticsLoading) return;

  window.__dayforitAnalyticsLoading = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=' + window.__dayforitMeasurementId;
  script.dataset.dayforitAnalytics = 'true';
  script.onload = function () {
    window.gtag('js', new Date());
    window.gtag('config', window.__dayforitMeasurementId, {
      'anonymize_ip': true
    });

    window.__dayforitAnalyticsLoaded = true;
    window.__dayforitAnalyticsLoading = false;
  };
  script.onerror = function () {
    window.__dayforitAnalyticsLoading = false;
  };

  document.head.appendChild(script);
};
`;

export const links = () => [
  { rel: 'icon', href: FAVICON },
  { rel: 'preconnect', href: 'https://api.open-meteo.com', crossOrigin: 'anonymous' },
  { rel: 'dns-prefetch', href: 'https://api.open-meteo.com' },
];

// Fallback for routes without their own meta (the SPA fallback / 404 page).
export const meta = () => NOT_FOUND_META;

/**
 * The HTML document for every page. Theme and body class come from the URL and the matched
 * route's `handle.bodyClass`, so they're correct in the prerendered HTML (no flash).
 */
export function Layout({ children }) {
  const { pathname } = useLocation();
  const matches = useMatches();
  const routeBodyClass = matches.findLast((match) => match.handle?.bodyClass)?.handle.bodyClass ?? '';

  return (
    <ThemeProvider pathname={pathname} routeBodyClass={routeBodyClass}>
      <html lang="en">
        <head>
          <meta charSet="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          {/* Static, first-party bootstrap code: must run before any analytics call. */}
          <script dangerouslySetInnerHTML={{ __html: GTAG_BOOTSTRAP }} />
          <Meta />
          <Links />
          <ThemeStyle />
        </head>
        <ThemedBody>
          {children}
          <ScrollRestoration />
          <Scripts />
        </ThemedBody>
      </html>
    </ThemeProvider>
  );
}

function usePageViewTracking() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Runs after the route's meta has updated document.title.
    trackPageView({ title: document.title, path: pathname });
  }, [pathname]);
}

export default function Root() {
  usePageViewTracking();
  return <SiteShell />;
}

// Shown while the SPA fallback (served for unknown URLs) hydrates.
export function HydrateFallback() {
  return (
    <main className="container" aria-busy="true">
      <h1>Day For It?</h1>
      <p className="loading" role="status" aria-live="polite">
        Loading page...
      </p>
    </main>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }

  console.error('Unhandled route error:', error);
  return <ErrorFallback error={error} />;
}
